import React, { useContext, useState } from 'react';

import _ from 'lodash';

import GridLayoutLeftBar from '../../common/layout/GridLayoutLeftBar';
import LegoAdminPageContext from '../../../pages/legoAdminPageContext';
import LegoContextSummary from '../LegoContextSummary';
import ModelContextEditor from '../ModelContextEditor';
import TopBarLayout from '../../common/layout/TopBarLayout';
import { IconButton } from '../../common/IconButton';
import { LegoStateBadge } from '../LegoStateBadge';
import { ImagePreview } from '../../images/ImagePreview';
import useAsyncEffect from '../../common/react-hooks/useAsyncEffect';
import FiltersBar from '../../common/filters-bar/FiltersBar';
import urls from '../../../../../components/interop/urls';
import BadgeId from '../../common/BadgeId';
import { stringToColor } from '../../common/colorUtils';
import BadgeColorHash from '../../common/BadgeColorHash';
import PublishedFuseboxThumbnail from './PublishedFuseboxThumbnail';
import LegoEditButton from '../LegoEditButton';
import SimilarityBadge from '../../common/SimilarityBadge';

function urlFuseboxThumbnail(modelId, year, other, fuseIndex) {
  // TODO: NASTY. Must be done in server with proper character sanitization
  let [marcaUrlSafe, modeloUrlSafe] = (modelId || '').toLowerCase().split('-');
  const modelObj = { idModelo: modelId, marcaUrlSafe, modeloUrlSafe };
  return urls.urlFuseboxThumbnail(modelObj, year, other, fuseIndex);
}


function getContextQuery({ ...context }) {
  //TODO: Repeated hack in LegoManager to fake make queries
  if (context.make) {
    context.modelId = { $regex: `^${context.make}-` };
    delete context.make;
  }
  return _.mapKeys(context, (val, key) => 'context.' + key);
}


async function fetchSimilarImageFuseboxes(searchSvc, boxDiagramImg, legoSvc) {
  let images = await searchSvc.find({ query: { category: 'fusebox-diagram', url: boxDiagramImg?.url, $limit: 100 } });
  console.log('images', images);
  let res = await legoSvc.find({
    query: {
      $or: [{ type: 'fuseboxTemplate' }, { type: 'fusebox' }], 'data.boxDiagramImg.url': { $in: _.map(images, 'url') }
    },
    $select: { 'data.fuses': false, 'data.boxDescription': false }
  });
  let imagesByUrl = _.keyBy(images, 'url');
  // Only leave the box with the image
  for (const l of res) {
    for (const box of l.data) {
      const matchingImage = imagesByUrl[box.boxDiagramImg?.url];

      if (matchingImage) {
        box.score = matchingImage.score;
      }
    }
  }
  return res;
}

// Clusters scoring below this [0,1] similarity to the fusebox's cluster are not shown as results.
const MIN_CLUSTER_SIMILARITY = 0.95;

async function fetchSimilarClusterFuseboxes(embeddingsSvc, legoSvc, clusterId) {
  // Rank other image clusters by similarity to the fusebox's cluster, then fetch the
  // templates/fuseboxes linked (via data.imageClusters) to any of those clusters.
  const res = await embeddingsSvc.find({ query: { clusterId, similarClusters: true } });
  const similarClusters = _.filter(res?.similarClusters, c => _.isNumber(c.score) && c.score >= MIN_CLUSTER_SIMILARITY);
  if (!similarClusters.length) return [];

  const scoreByClusterId = _.fromPairs(_.map(similarClusters, c => [c.clusterId, c.score]));

  const legos = await legoSvc.find({
    query: {
      $or: [{ type: 'fuseboxTemplate' }, { type: 'fusebox' }],
      'data.imageClusters': { $in: _.map(similarClusters, 'clusterId') },
    },
    $select: { 'data.fuses': false, 'data.boxDescription': false }
  });

  // Score each box by the most similar of its linked clusters (boxes without a ranked cluster
  // keep score undefined and are filtered out, like in the image-similarity search).
  for (const lego of legos) {
    for (const box of lego.data) {
      const scores = _.filter(_.map(box.imageClusters || [], cid => scoreByClusterId[cid]), _.isNumber);
      if (scores.length) box.score = _.round(_.max(scores), 3);
      if (_.includes(box.imageClusters, clusterId)) box.sameCluster = true;
    }
  }
  return legos;
}

async function fetchSimilarContextFuseboxes(legoSvc, context) {
  return await legoSvc.find({
    query: {
      $or: [{ type: 'fuseboxTemplate' }, { type: 'fusebox' }],
      $select: { 'data.fuses': false, 'data.boxDescription': false },
      ...getContextQuery(context),
    }
  });
}

function FuseboxTemplateResult({ lego, mergedContexts, firstLegoFusebox, fuseboxIndex, onClick, selected, similarityScore, ...other }) {
  let box = (lego.data || [])[fuseboxIndex];

  if (box) {
    let { boxDiagramImg, boxAspectRatio } = box;

    const isTemplate = lego.type === 'fuseboxTemplate';
    const usesTemplate = !!box.templateId;
    let bkg = isTemplate ? 'bg-gris-blue' : (box.templateId ? 'bg-light' : (lego.state === 'inprogress' ? 'bg-light-warn' : 'bg-light'));

    if (selected && isTemplate)
      bkg = 'bg-light-success'

    const { modelId, year, ...otherContextSpecs } = lego.context[0];
    const boxThumbnail = urlFuseboxThumbnail(modelId, year, otherContextSpecs, fuseboxIndex);
    const templateThumbnail = <ImagePreview url={boxDiagramImg?.url || boxThumbnail} img100 contain zoom />;
    const thumbnailFbox = (firstLegoFusebox && firstLegoFusebox.state === 'published') ? <PublishedFuseboxThumbnail context={firstLegoFusebox.context[0]} locale={firstLegoFusebox.locales?.[0]} boxIndex={fuseboxIndex} /> : null;

    return <span className={`d-block align-top pos-relative p-1 pb-0 rounded ${bkg} text-center`} {...other}>
      <span className={'float-bottom-right zoom-75'}>
        {similarityScore && box.sameCluster
          ? <span className={'badge badge-success mr-2'} title={'Linked to the same image cluster'}>Same cluster</span>
          : (similarityScore && _.isNumber(box.score)
            ? <SimilarityBadge value={box.score} title={'Cluster similarity'} className={'mr-2'} />
            : <span className={'small text-black-50 mr-2'}>{box.score}</span>)}
        <LegoStateBadge state={lego.state} />
      </span>

      <div className='mb-1'>
        {templateThumbnail}
        {thumbnailFbox}
      </div>

      <div className="text-left" style={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <LegoContextSummary context={mergedContexts || lego.context} limit={3} />
      </div>

      {selected ? <div><span className={'badge badge-success mt-1'}>Current template</span></div> : null}

      {!selected && onClick ? <div>
        <IconButton icon={'check_circle_outline'} className={''} level={'primary'} onClick={onClick}>
          {isTemplate ? 'Apply' : (usesTemplate ? 'Use same template' : 'Refactor')}
        </IconButton>
      </div> : null}
    </span>;
  }
}


export function FuseboxTemplateFinder({ onSelected, fuseboxData, currentTemplateId, currentLegoId, currentClusterIds = [], context: [fuseboxCtx] }) {
  let { page } = useContext(LegoAdminPageContext);
  let [context, setContext] = useState({ modelId: fuseboxCtx?.modelId || 'Ford-Focus' });
  let [fuseboxes, setFuseboxes] = useState(null);

  let { boxDiagramImg } = fuseboxData;

  const hasClusters = !!currentClusterIds?.length;
  const hasImage = !!boxDiagramImg?.url;

  let [searchMode, setSearchMode] = useState('context');

  useAsyncEffect(async (isActive) => {
    const searchSvc = page.service('services/data/image-search');
    const legoSvc = page.service('services/legos');
    const embeddingsSvc = page.service('services/data/fusebox-model-embeddings');

    setFuseboxes(null);

    let result;
    if (searchMode === 'cluster' && hasClusters) {
      result = await fetchSimilarClusterFuseboxes(embeddingsSvc, legoSvc, currentClusterIds[0]).catch(alert);
    } else if (searchMode === 'image' && hasImage) {
      result = await fetchSimilarImageFuseboxes(searchSvc, boxDiagramImg, legoSvc).catch(alert);
    } else if (context.modelId || context.make) {
      result = await fetchSimilarContextFuseboxes(legoSvc, context).catch(alert);
    }

    // Discard the result if a newer effect run has superseded this one (deps changed mid-fetch).
    if (isActive()) setFuseboxes(result);
  }, [JSON.stringify(context), searchMode]);

  let results = <div className={'full-screen-center'}>Loading fusebox templates...</div>;

  if (fuseboxes) {
    // Make pairs [Lego, fusebox index inside lego] to handle fusebox legos with multiple fuseboxes
    let individualFuxeboxes = _.flatten(_.map(fuseboxes, lego => _.map(lego.data, (fbx, i) => [lego, i])));

    let sortedBoxes;
    if (searchMode === 'cluster') {
      individualFuxeboxes = _.filter(individualFuxeboxes, ([lego, i]) => lego.data[i].score !== undefined);

      // Cluster similarity is on the [0,1] scale where higher is more similar.
      sortedBoxes = _.sortBy(individualFuxeboxes, ([lego, i]) => -lego.data[i].score);
    } else if (searchMode === 'image' && hasImage) {
      individualFuxeboxes = _.filter(individualFuxeboxes, ([lego, i]) => lego.data[i].score !== undefined);

      sortedBoxes = _.sortBy(individualFuxeboxes, ([lego, i]) => {
        const isSameModel = lego.context[0].modelId === context.modelId;
        return lego.data[i].score + (isSameModel ? 0 : 10);
      });
    } else {
      // Sort them by closest fusebox aspect ratio + distance between model years
      const sortByPublished = ([{ state, data }, i]) => (state === 'published' && !data[i].templateId) ? 0 : 1;

      const sortBySimilarityAndYear = ([{ data, context: tempCtx }, i]) => {
        let year = (tempCtx[0] || tempCtx).year || 0;
        const ratioYear = Math.abs(year - fuseboxCtx.year);

        let ratioA = data[i].boxAspectRatio || -1;
        let ratioB = fuseboxData.boxAspectRatio || 0;

        // TODO: Show published first
        return Math.abs(ratioA - ratioB) + ratioYear / 30;
      };

      sortedBoxes = _.sortBy(individualFuxeboxes, sortByPublished, sortBySimilarityAndYear);
    }

    // Separate templates from fuseboxes
    let templates = _.filter(sortedBoxes, ([f, i]) => (f.type === 'fuseboxTemplate' || f.data[i].templateId) && f._id !== currentLegoId);
    let otherFuseboxes = _.filter(sortedBoxes, ([f, i]) => f.type === 'fusebox' && !f.data[i].templateId && f._id !== currentLegoId);

    // Normalized remap key: an empty map counts as no remap, and entries are sorted so the same
    // logical remap always yields the same key regardless of how its keys happened to be ordered.
    const remapKey = box => _.isEmpty(box.templateRenameMap) ? '' : ' MAP ' + JSON.stringify(_.sortBy(_.toPairs(box.templateRenameMap), 0));
    let groupedByTemplate = _.groupBy(templates, ([fb, i]) => fb.type === 'fuseboxTemplate' ? String(fb._id) : String(fb.data[i].templateId) + remapKey(fb.data[i]));

    // Represent each group by the template lego itself when it's part of the group (stable sort
    // keeps the score/relevance order among the rest), so Apply applies the template directly.
    groupedByTemplate = _.mapValues(groupedByTemplate, group => _.sortBy(group, ([fb]) => fb.type === 'fuseboxTemplate' ? 0 : 1));

    results = <div className={'bg-light'}>
      <div className={'text-white bg-gris-blue px-2 py-1'}>Templates and fusebox using templates</div>

      {_.map(groupedByTemplate, (groupLegos, key) => {
        let [firstLego, firstIndex] = groupLegos[0];
        const templateId = firstLego.data[firstIndex].templateId || firstLego._id;
        const usesCurrentTemplate = currentTemplateId && currentTemplateId === templateId;
        const hasSameRemapping = _.isEqual(fuseboxData.templateRenameMap, firstLego.data[firstIndex].templateRenameMap);
        const isRemapped = _.isEmpty(firstLego.data[firstIndex].templateRenameMap);
        const selected = usesCurrentTemplate && (hasSameRemapping || firstLego.type === 'fuseboxTemplate');
        // Use full 24 character mongo ID for coloring, since Mongo IDs are always 24-character hex strings

        const mergedContexts = _.flatMap(_.map(groupLegos, ([lego, i]) => lego.context));
        const bestSimilarityLego = _.maxBy(mergedContexts, (ctx) => ctx.similarityScore);
        const firstLegoFusebox = _.find(groupLegos, ([lego]) => lego.type === 'fusebox')?.[0];

        return <span className={`border ${selected ? 'border-success bg-light-success border-2' : 'border-dark bg-dark'} rounded p-1 inline-block m-2 align-top`}
          key={key}>
          <div className={''}>
            {!selected ? <span>
              <IconButton icon={'check_circle_outline'} level={'success'} onClick={() => onSelected(firstLego, firstIndex)}>
                Apply
              </IconButton>
            </span> : null}

            <LegoEditButton legoId={templateId}>
              <BadgeColorHash id={templateId} />
            </LegoEditButton>

            {groupLegos.length > 1 ? <span className={'ml-1 badge badge-dark'}>{groupLegos.length}</span> : null}
            {!isRemapped ? <span className={'badge badge-info mt-1'}>REMAPPED</span> : null}
          </div>

          <FuseboxTemplateResult key={firstLego._id} lego={firstLego} firstLegoFusebox={firstLegoFusebox} mergedContexts={mergedContexts} fuseboxIndex={firstIndex} selected={usesCurrentTemplate && hasSameRemapping} similarityScore={searchMode === 'cluster'} />

        </span>
      })}

      {!templates.length ? <div className={'p-2'}>There are no templates. Check in the <strong>make</strong></div> : null}

      <div className={'bg-light-dark'}>
        <div className={'text-white bg-dark px-2 py-1'}>Fuseboxes that can be refactored into templates</div>
        {_.map(otherFuseboxes, ([lego, i]) => {
          return <span className='d-inline-block m-1'>
            <FuseboxTemplateResult key={`${lego._id}_${i}`} lego={lego} fuseboxIndex={i} onClick={() => onSelected(lego, i)} similarityScore={searchMode === 'cluster'} />
          </span>;
        })}
      </div>
    </div>;
  }

  return <GridLayoutLeftBar style={{ left: '0', top: '0' }}>
    <div className={'bg-light p-2'} style={{ width: '450px' }}>
      <h5>Finding match for fusebox:</h5>

      <LegoContextSummary context={fuseboxCtx} />

      <div className={'mt-3'}>
        <ImagePreview url={boxDiagramImg?.url} fluid zoom />
      </div>
    </div>

    <TopBarLayout>
      <FiltersBar>
        <ul className={'nav nav-tabs align-self-end'} style={{marginBottom: "-8px"}}>
          {[
            { key: 'cluster', label: 'By cluster similarity', disabled: !hasClusters, title: hasClusters ? null : 'The fusebox has no linked image clusters' },
            { key: 'image', label: 'By image similarity', disabled: !hasImage, title: hasImage ? null : 'The fusebox has no diagram image' },
            { key: 'context', label: 'By context' },
          ].map(({ key, label, disabled, title }) => <li className={'nav-item'} key={key}>
            <a href={'#'} title={title}
               className={`nav-link p-1 px-3 ${searchMode === key ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
               onClick={e => { e.preventDefault(); if (!disabled) setSearchMode(key); }}>
              {label}
            </a>
          </li>)}
        </ul>
        {searchMode === 'context' ? <ModelContextEditor value={context} onChange={v => setContext(v)} className={'flex-grow-1 ml-3'} /> : null}
      </FiltersBar>
      {results}
    </TopBarLayout>
  </GridLayoutLeftBar>;
}
